import { FormArray, FormControl, FormGroup, Validators, ɵFormGroupValue } from '@angular/forms';
import { CheckoutFlightOut, InsurancePriceForTourist } from '@appTypes/api.types';
import {
  dateDifferenceWithField,
  maxDate,
  maxDateThanField,
  maxLengthWithMessage,
  minDate,
  minDateThanField,
  regExpPattern,
  TOURIST_NAME_LETTER_MESSAGE,
  TOURIST_NAME_LETTER_PATTERN,
  TOURIST_NAME_PATTERN
} from '@core/misc/validators.utils';
import { addMonths, differenceInDays, subYears } from 'date-fns';
import { distinctUntilChanged } from 'rxjs';

export class CheckOutTouristManuelFlightTypedForm {
  date = new FormControl<Date | null>(null);
  time = new FormControl<string | null>(null);
  flightNumber = new FormControl<string[] | null>(null);
  terminalCode = new FormControl<string[] | null>(null);
}
export class CheckoutTouristTransferTypedForm {
  id = new FormControl<string | null>(null);
  selectedTransferPriceId = new FormControl<string | null>(null);
}
export type ICheckoutTouristTransferFormValues = ɵFormGroupValue<CheckoutTouristTransferTypedForm>;

export class CheckoutTouristTypedForm {
  id = new FormControl<string | null>(null);
  age = new FormControl<number | null>(null);
  isAdult = new FormControl<boolean | null>(null);
  isMale = new FormControl<boolean | null>(null, Validators.required);
  // R-CK30 (website-web#123): the letter check comes first - both report `pattern`, the later one wins, so a name with other characters keeps
  // the "Only Latin letters ..." reason and only a letter-less name (".", "-", "'") shows "A name must contain at least one Latin letter"
  firstName = new FormControl<string | null>(null, [
    Validators.required,
    regExpPattern(TOURIST_NAME_LETTER_PATTERN, TOURIST_NAME_LETTER_MESSAGE),
    regExpPattern(TOURIST_NAME_PATTERN, 'Only Latin letters, space, dot, apostrophe and hyphen are allowed'),
    maxLengthWithMessage(150, 'A name may have at most 150 characters')
  ]);
  lastName = new FormControl<string | null>(null, [
    Validators.required,
    regExpPattern(TOURIST_NAME_LETTER_PATTERN, TOURIST_NAME_LETTER_MESSAGE),
    regExpPattern(TOURIST_NAME_PATTERN, 'Only Latin letters, space, dot, apostrophe and hyphen are allowed'),
    maxLengthWithMessage(150, 'A name may have at most 150 characters')
  ]);
  birthDate = new FormControl<Date | null>(null, [
    Validators.required,
    maxDate(),
    minDate(subYears(new Date(), 101), 'Age must be lower than 100'),
    maxDateThanField('passportIssue', 'The date must be before passport issue date')
  ]);

  // R-CK9a (STG 28.10.2026): the checkout service gives the signed-in contact's country (else the agency's) from the token; null = blank and
  // required. Never a fixed country: a form built without an argument starts blank too.
  citizenship: FormControl<string | null>;
  passportNumber = new FormControl<string | null>(null, [
    Validators.required,
    regExpPattern(
      /^[0-9 A-Za-z]{5,50}$/,
      'Passport number must be between 5 and 50 characters, using only alphanumeric characters'
    )
  ]);
  passportIssue = new FormControl<Date | null>(null, [
    Validators.required,
    maxDate(),
    minDateThanField('birthDate', 'The date must be after birth date')
  ]);
  passportExpiry = new FormControl<Date | null>(null, [
    Validators.required,
    minDate(addMonths(new Date(), 6), 'Expiry date must be more than 6 months from today'),
    dateDifferenceWithField(
      'passportIssue',
      (passportExpiry, passportIssue) => differenceInDays(passportExpiry, passportIssue) > 3653, //10 yars + 1 day = 3653
      'The validity of the passport must be no more than 10 years from the date of issue'
    )
  ]);
  tin = new FormControl<string | null>(null);

  selectedVisaId = new FormControl<string | null>(null);
  flights = new FormControl<CheckoutFlightOut[] | null>(null);
  flightIds = new FormControl<string[] | null>(null);
  insurancePrice = new FormControl<InsurancePriceForTourist | null>(null);
  departureFlightInfo = new FormGroup(new CheckOutTouristManuelFlightTypedForm());
  arrivalFlightInfo = new FormGroup(new CheckOutTouristManuelFlightTypedForm());
  transfers = new FormArray<FormGroup<CheckoutTouristTransferTypedForm>>([]);

  constructor(defaultCitizenship: string | null = null) {
    this.citizenship = new FormControl<string | null>(defaultCitizenship, Validators.required);
    // The 10-year rule sits on the expiry but reads the issue date: re-check it when the issue date changes (PASSVALID-001).
    // distinctUntilChanged stops the loop through dateDifferenceWithField, which re-validates an issue date that has an error.
    this.passportIssue.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(() => this.passportExpiry.updateValueAndValidity());
  }
}
export type ICheckoutTouristFormValues = ɵFormGroupValue<CheckoutTouristTypedForm>;

export class CheckoutHotelTypedForm {
  note = new FormControl<string | null>(null);
  agentConfirmationNumber = new FormControl<string | null>(null);
  hotelPriceId = new FormControl<string | null>(null);
  touristIds = new FormArray<FormControl<string | null>>([]);
}
export type ICheckoutHotelFormValues = ɵFormGroupValue<CheckoutHotelTypedForm>;

export class CheckoutTypedForm {
  tourists = new FormArray<FormGroup<CheckoutTouristTypedForm>>([]);
  hotels = new FormArray<FormGroup<CheckoutHotelTypedForm>>([]);

  agentCommissionAmount = new FormControl<number | null>(null);
  agentCommissionPercent = new FormControl<number | null>(null);
  totalPrice = new FormControl<number | null>(null);
  priceCurrency = new FormControl<string | null>(null);
  note = new FormControl<string | null>(null);
  /** Full phone number including the dial code (composed by app-phone-input). */
  contactPhone = new FormControl<string | null>(null, Validators.required);
  reservationId = new FormControl<string | null>(null);
}
export type ICheckoutFormValues = ɵFormGroupValue<CheckoutTypedForm>;
